/**
 * Sign in / create account — the standalone page, for cold visits (header
 * link, route guards). The interview flow no longer lands here: the launch
 * screen embeds the same <AuthForm> in its own card so that auth and scan are
 * one screen. This page is the general-purpose wrapper around that form.
 */
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { isKnownNext } from '@ideabrief/core';
import { AuthForm, type AuthMode } from '../components/AuthForm';
import { postAuthDestination } from '../lib/draft';
import { Button, Logo, Wordmark } from '../components/ui';

export function Auth() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();

  // `next` is an allowlist KEY, never a path — see resolveNext() in core. It is
  // attacker-controllable, so it is resolved, never navigated to directly.
  // With no valid `next`, the destination is draft-aware: a user who signs in
  // cold while an interview draft sits in localStorage almost certainly came
  // back to finish it, so they land on /launch instead of an empty dashboard.
  const nextParam = params.get('next');
  /** Arrived mid-flow (e.g. an emailed reset link that carried next=) rather than cold. */
  const fromFlow = isKnownNext(nextParam);

  // Someone sent here mid-flow has just spent eight questions and almost
  // certainly has no account yet — landing them on "Welcome back" and a
  // password they never set is the wrong first impression. Cold visits to /auth
  // (header link) still default to sign-in, where a returning user expects to
  // be. An explicit ?mode= always wins over both.
  const modeParam = params.get('mode');
  const mode: AuthMode =
    modeParam === 'up' ? 'up' : modeParam === 'in' ? 'in' : fromFlow ? 'up' : 'in';
  const isSignUp = mode === 'up';

  const [forgot, setForgot] = useState(false);

  function setMode(target: AuthMode) {
    // `next` must survive a tab switch. Dropping it here would strand a user
    // who toggled to "Sign in" with their interview draft orphaned in
    // localStorage. The mode is always written explicitly so it also survives
    // the fromFlow default above.
    const query: Record<string, string> = { mode: target };
    if (nextParam && isKnownNext(nextParam)) query.next = nextParam;
    setParams(query, { replace: true });
  }

  const header = (
    <>
      {!forgot && (
        <div
          role="tablist"
          aria-label="Sign in or create an account"
          style={{
            display: 'flex',
            gap: 4,
            background: 'var(--gutter)',
            border: '1px solid var(--line2)',
            borderRadius: 11,
            padding: 4,
            marginBottom: 26,
          }}
        >
          {(['in', 'up'] as const).map((m) => {
            const active = mode === m;
            return (
              <button
                key={m}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setMode(m)}
                style={{
                  flex: 1,
                  border: 'none',
                  borderRadius: 8,
                  padding: '10px 14px',
                  fontSize: 14,
                  fontWeight: 500,
                  cursor: 'pointer',
                  background: active ? '#fff' : 'transparent',
                  color: active ? 'var(--ink)' : 'var(--muted)',
                  boxShadow: active ? '0 1px 2px rgba(24,23,32,.10)' : 'none',
                }}
              >
                {m === 'in' ? 'Sign in' : 'Create account'}
              </button>
            );
          })}
        </div>
      )}

      <h1
        style={{
          margin: 0,
          fontSize: 29,
          lineHeight: 1.16,
          letterSpacing: '-.03em',
          fontWeight: 600,
        }}
      >
        {forgot
          ? 'Reset your password.'
          : isSignUp
            ? 'Create your account.'
            : 'Welcome back.'}
      </h1>
      <p
        style={{
          margin: '12px 0 0',
          fontSize: 15.5,
          lineHeight: 1.6,
          color: 'var(--muted)',
        }}
      >
        {forgot
          ? "Enter your email address and we'll send you a link to choose a new password."
          : fromFlow
            ? isSignUp
              ? 'Your brief is saved. Create your account, then run the scan from your brief.'
              : 'Your brief is saved. Sign in to pick up where you left off.'
            : isSignUp
              ? 'One account holds every idea you evaluate — scans, research status and reports in one place.'
              : 'Sign in to track research, finish refinements and download your reports.'}
      </p>
    </>
  );

  return (
    <AuthShell>
      <AuthForm
        mode={mode}
        onModeChange={setMode}
        forgot={forgot}
        onForgotChange={setForgot}
        onAuthenticated={() =>
          navigate(postAuthDestination(nextParam), { replace: true })
        }
        consentScope="signup"
        consentLabel={
          <>
            I agree to the <a href="/privacy">privacy policy</a> and{' '}
            <a href="/terms">terms</a>.
          </>
        }
        submitLabel={{ in: 'Sign in', up: 'Create account' }}
        resetRedirectPath={fromFlow ? `/auth/reset?next=${nextParam}` : '/auth/reset'}
        header={header}
        variant="page"
        showGoogle
      />
    </AuthShell>
  );
}

function AuthShell({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        animation: 'ibFadeIn .3s ease both',
      }}
    >
      <header style={{ borderBottom: '1px solid var(--line2)' }}>
        <div
          style={{
            maxWidth: 1080,
            margin: '0 auto',
            padding: '14px 24px',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <button
            onClick={() => navigate('/')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 9,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
              color: 'var(--ink)',
            }}
          >
            <Logo />
            <Wordmark />
          </button>
          <button
            onClick={() => navigate('/')}
            style={{
              marginLeft: 'auto',
              background: 'none',
              border: 'none',
              fontSize: 13.5,
              color: 'var(--muted)',
              cursor: 'pointer',
              padding: 0,
            }}
          >
            ← Back
          </button>
        </div>
      </header>

      <div
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '48px 20px 90px',
        }}
      >
        <div
          style={{
            width: '100%',
            maxWidth: 420,
            animation: 'ibRise .3s cubic-bezier(.2,.7,.3,1) both',
          }}
        >
          {children}
          <p
            style={{
              margin: '14px 0 0',
              fontSize: 12.5,
              lineHeight: 1.6,
              color: 'var(--muted)',
              textAlign: 'center',
            }}
          >
            Signing in only tells us your name and email. It doesn't give us
            access to anything else in your account.
          </p>
        </div>
      </div>
    </div>
  );
}
